import type { AuthOtpResponse } from '@/interfaces/AuthOtp';
import type { SelectOption } from '@/interfaces/Select';

export const enum AuthPhoneProvider {
    Exolve = 'exolve',
    FirebasePhone = 'firebase_phone',
    Unsupported = 'unsupported',
}

export type AuthPhonePolicyCountry = {
    id: string;
    label: string;
    labelRussian: string;
    flag: string;
    countryCode: string;
    authProvider: AuthPhoneProvider;
    enabled: boolean;
    disabledReason?: string;
};

export type AuthPhonePolicy = {
    schemaVersion: 'auth_phone_policy_v1';
    defaultCountryId: string;
    countries: AuthPhonePolicyCountry[];
    providers: Record<AuthPhoneProvider.Exolve | AuthPhoneProvider.FirebasePhone, { enabled: boolean }>;
};

const FIREBASE_PHONE_OTP_LENGTH = 6;
const EXOLVE_PHONE_OTP_LENGTH = 4;
const FALLBACK_COUNTRY: AuthPhonePolicyCountry = {
    id: 'RU',
    label: 'Russia',
    labelRussian: 'Россия',
    flag: '🇷🇺',
    countryCode: '+7',
    authProvider: AuthPhoneProvider.Exolve,
    enabled: true,
};

export const FALLBACK_AUTH_PHONE_POLICY: AuthPhonePolicy = {
    schemaVersion: 'auth_phone_policy_v1',
    defaultCountryId: FALLBACK_COUNTRY.id,
    countries: [FALLBACK_COUNTRY],
    providers: {
        [AuthPhoneProvider.Exolve]: {
            enabled: true,
        },
        [AuthPhoneProvider.FirebasePhone]: {
            enabled: false,
        },
    },
};

export const buildAuthPhonePolicyCountryById = (policy: AuthPhonePolicy) => {
    const countryById = new Map<string, AuthPhonePolicyCountry>();

    policy.countries.forEach((country) => {
        countryById.set(country.id, country);
    });

    return countryById;
};

export const getAuthPhonePolicyCountry = ({
    countryById,
    countryOptionId,
}: {
    countryById: Map<string, AuthPhonePolicyCountry>;
    countryOptionId: string;
}) => countryById.get(countryOptionId) ?? null;

export const getAuthPhoneCountrySelectOptions = (policy: AuthPhonePolicy): SelectOption[] =>
    policy.countries.map((country) => ({
        value: country.id,
        label: `${country.flag} ${country.labelRussian || country.label} ${country.countryCode}`,
    }));

export const getDefaultAuthPhonePolicyCountryId = ({ policy }: { policy: AuthPhonePolicy }) =>
    policy.countries.some((country) => country.id === policy.defaultCountryId) ? policy.defaultCountryId : FALLBACK_COUNTRY.id;

export const getAuthPhoneProvider = ({
    countryById,
    countryOptionId,
}: {
    countryById: Map<string, AuthPhonePolicyCountry>;
    countryOptionId: string;
}) => getAuthPhonePolicyCountry({ countryById, countryOptionId })?.authProvider ?? AuthPhoneProvider.Unsupported;

export const getAuthOtpCodeLength = (provider: AuthPhoneProvider) =>
    provider === AuthPhoneProvider.FirebasePhone ? FIREBASE_PHONE_OTP_LENGTH : EXOLVE_PHONE_OTP_LENGTH;

export const buildFirebasePhoneOtpResponse = (): AuthOtpResponse => ({
    isRegister: false,
    primaryTransport: 'sms',
    deliveryTransport: 'sms',
    deliveryStep: 'initial',
    nextStep: null,
    nextTransport: null,
});
